/**
 * Axios 请求缓存工具
 * 支持内存缓存和localStorage持久化缓存
 */

class AxiosCache {
  constructor(options = {}) {
    this.cache = new Map(); // 内存缓存
    this.pendingRequests = new Map(); // 待处理的请求，用于防止重复请求
    this.config = {
      maxAge: options.maxAge || 5 * 60 * 1000, // 默认缓存5分钟
      maxSize: options.maxSize || 100, // 最大缓存条目数
      storage: options.storage || false, // 是否使用localStorage持久化
      storagePrefix: options.storagePrefix || 'axios_cache_', // localStorage键前缀
      exclude: options.exclude || [], // 排除的URL模式（正则）
      debug: options.debug || false, // 调试模式
    };
  }

  /**
   * 生成缓存键
   */
  generateKey(config) {
    const { method = 'get', url, params = {}, data = {} } = config;
    const sortedParams = JSON.stringify(params, Object.keys(params).sort());
    const sortedData = JSON.stringify(data, Object.keys(data).sort());
    return `${method.toLowerCase()}:${url}?${sortedParams}&${sortedData}`;
  }

  /**
   * 检查URL是否应该被排除缓存
   */
  shouldExclude(url) {
    return this.config.exclude.some((pattern) => {
      if (pattern instanceof RegExp) {
        return pattern.test(url);
      }
      return url.includes(pattern);
    });
  }

  /**
   * 从缓存获取数据
   */
  get(key) {
    // 先检查内存缓存
    if (this.cache.has(key)) {
      const item = this.cache.get(key);
      if (Date.now() < item.expiry) {
        this.log('从内存缓存获取:', key);
        return item.data;
      } else {
        this.cache.delete(key);
      }
    }

    // 检查localStorage
    if (this.config.storage) {
      try {
        const storageKey = this.config.storagePrefix + key;
        const item = localStorage.getItem(storageKey);
        if (item) {
          const parsed = JSON.parse(item);
          if (Date.now() < parsed.expiry) {
            this.log('从localStorage获取:', key);
            // 恢复到内存缓存
            this.cache.set(key, parsed);
            return parsed.data;
          } else {
            localStorage.removeItem(storageKey);
          }
        }
      } catch (e) {
        console.error('读取localStorage缓存失败:', e);
      }
    }

    return null;
  }

  /**
   * 设置缓存
   */
  set(key, data, maxAge = this.config.maxAge) {
    const item = {
      data,
      expiry: Date.now() + maxAge,
      timestamp: Date.now(),
    };

    // 检查缓存大小限制
    if (this.cache.size >= this.config.maxSize) {
      // 删除最旧的缓存项
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
      if (this.config.storage) {
        try {
          localStorage.removeItem(this.config.storagePrefix + firstKey);
        } catch (e) {
          console.error('删除localStorage缓存失败:', e);
        }
      }
    }

    // 保存到内存
    this.cache.set(key, item);
    this.log('设置缓存:', key);

    // 保存到localStorage
    if (this.config.storage) {
      try {
        const storageKey = this.config.storagePrefix + key;
        localStorage.setItem(storageKey, JSON.stringify(item));
      } catch (e) {
        console.error('保存到localStorage失败:', e);
        // localStorage可能已满，尝试清理过期项
        this.clearExpiredStorage();
      }
    }
  }

  /**
   * 删除指定缓存
   */
  delete(key) {
    this.cache.delete(key);
    if (this.config.storage) {
      try {
        localStorage.removeItem(this.config.storagePrefix + key);
      } catch (e) {
        console.error('删除localStorage缓存失败:', e);
      }
    }
  }

  /**
   * 清空所有缓存
   */
  clear() {
    this.cache.clear();
    if (this.config.storage) {
      try {
        const keys = Object.keys(localStorage);
        keys.forEach((key) => {
          if (key.startsWith(this.config.storagePrefix)) {
            localStorage.removeItem(key);
          }
        });
      } catch (e) {
        console.error('清空localStorage缓存失败:', e);
      }
    }
    this.log('已清空所有缓存');
  }

  /**
   * 清理过期的localStorage缓存
   */
  clearExpiredStorage() {
    if (!this.config.storage) return;

    try {
      const keys = Object.keys(localStorage);
      const now = Date.now();
      keys.forEach((key) => {
        if (key.startsWith(this.config.storagePrefix)) {
          const item = JSON.parse(localStorage.getItem(key));
          if (now >= item.expiry) {
            localStorage.removeItem(key);
          }
        }
      });
    } catch (e) {
      console.error('清理过期localStorage缓存失败:', e);
    }
  }

  /**
   * 获取待处理的请求
   */
  getPendingRequest(key) {
    return this.pendingRequests.get(key);
  }

  /**
   * 设置待处理的请求
   */
  setPendingRequest(key, promise) {
    this.pendingRequests.set(key, promise);
    // 请求完成后自动清理
    promise.finally(() => {
      this.pendingRequests.delete(key);
    });
  }

  /**
   * 调试日志
   */
  log(...args) {
    if (this.config.debug) {
      console.log('[AxiosCache]', ...args);
    }
  }

  /**
   * 获取缓存统计信息
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.config.maxSize,
      pendingRequests: this.pendingRequests.size,
    };
  }
}

/**
 * 检查URL参数并清除缓存
 */
export function checkAndClearCacheFromURL(cache, paramName = 'clearCache') {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has(paramName)) {
    cache.clear();
    console.log('[AxiosCache] 已通过URL参数清除所有缓存');
    
    // 移除URL参数，避免刷新页面时再次清除
    urlParams.delete(paramName);
    const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '') + window.location.hash;
    window.history.replaceState({}, '', newUrl);
    
    return true;
  }
  return false;
}

/**
 * 创建axios缓存拦截器
 */
export function setupAxiosCache(axiosInstance, options = {}) {
  const cache = new AxiosCache(options);

  // 请求拦截器
  axiosInstance.interceptors.request.use(
    (config) => {
      // 只缓存GET请求（默认）
      const cacheableMethods = options.methods || ['get'];
      if (!cacheableMethods.includes(config.method?.toLowerCase())) {
        return config;
      }

      // 检查是否强制刷新
      if (config.forceRefresh) {
        return config;
      }

      // 检查是否应该排除
      if (cache.shouldExclude(config.url)) {
        return config;
      }

      const key = cache.generateKey(config);

      // 检查是否有缓存
      const cachedData = cache.get(key);
      if (cachedData) {
        // 返回缓存数据，取消实际请求
        config.adapter = () => {
          return Promise.resolve({
            data: cachedData,
            status: 200,
            statusText: 'OK',
            headers: {},
            config,
            request: {},
            fromCache: true,
          });
        };
      } else {
        // 检查是否有相同的请求正在处理
        const pendingRequest = cache.getPendingRequest(key);
        if (pendingRequest) {
          config.adapter = () => pendingRequest;
        } else {
          // 标记这个请求的key，在响应拦截器中使用
          config._cacheKey = key;
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // 响应拦截器
  axiosInstance.interceptors.response.use(
    (response) => {
      // 如果这是从缓存返回的，直接返回
      if (response.fromCache) {
        return response;
      }

      const config = response.config;

      // 只缓存GET请求
      const cacheableMethods = options.methods || ['get'];
      if (!cacheableMethods.includes(config.method?.toLowerCase())) {
        return response;
      }

      // 检查是否应该排除
      if (cache.shouldExclude(config.url)) {
        return response;
      }

      // 只缓存成功的响应
      if (response.status === 200 && config._cacheKey) {
        const maxAge = config.cacheMaxAge || options.maxAge;
        cache.set(config._cacheKey, response.data, maxAge);
      }

      return response;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // 返回缓存实例，方便手动操作
  return cache;
}

export default AxiosCache;
